import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AdminLogin from "./AdminLogin";
import AdminSignUp from "./AdminSignUp";
import AdminLayout from "./AdminLayout";
import AdminDashboard from "./AdminDashboard";
import AdminUserApproval from "./AdminUserApproval";
import AdminLoginHistory from "./AdminLoginHistory";
import AdminChangeRequests from "./AdminChangeRequests";
import AdminNotificationPanel from "./AdminNotificationPanel";
import AdminStaffChanges from "./AdminStaffChanges";
import AdminStaffSubmitChange from "./AdminStaffSubmitChange";
import AdminMyChanges from "./AdminMyChanges";
import AdminContentManager from "./AdminContentManager";
import AdminFooterEditor from "./AdminFooterEditor";
import AdminNavbarEditor from "./AdminNavbarEditor";
import AdminVisaDetailEditor from "./AdminVisaDetailEditor";
import AdminRefundPolicyEditor from "./AdminRefundPolicyEditor";
import AdminRequirementsEditor from "./AdminRequirementsEditor";
import AdminVisaCareerEditor from "./AdminVisaCareerEditor";
import AdminScheduleMeetingEditor from "./AdminScheduleMeetingEditor";
import AdminGovernmentFeeEditor from "./AdminGovernmentFeeEditor";
import AdminGovtFeeHistoryEditor from "./AdminGovtFeeHistoryEditor";
import AdminJobApplications from "./AdminJobApplications";
import AdminMeetingBookings from "./AdminMeetingBookings";
import AdminReviews from "./AdminReviews";
import AdminEmailSettingsEditor from "./AdminEmailSettingsEditor";
import AdminHeroEditor from "./AdminHeroEditor";
import AdminEventEditor from "./AdminEventEditor";
import AdminPassportEditor from "./AdminPassportEditor";

import { API_BASE_URL } from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminApp() {
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const storedUser = sessionStorage.getItem("tvc_user");
    const token = sessionStorage.getItem("tvc_token");
    if (storedUser && token) {
      (async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            sessionStorage.removeItem("tvc_user");
            sessionStorage.removeItem("tvc_token");
            setLoading(false);
            return;
          }
          const data = await res.json();
          const freshUser = data.user || JSON.parse(storedUser);
          sessionStorage.setItem("tvc_user", JSON.stringify(freshUser));
          setUser(freshUser);
          const rolePath = freshUser.role.toLowerCase();
          const pathParts = location.pathname.split("/");
          const lastPart = pathParts[pathParts.length - 1];
          if (lastPart && lastPart !== "login" && lastPart !== "signup" && lastPart !== rolePath) {
            setActiveSection(`/${rolePath}/${lastPart}`);
          } else {
            setActiveSection(`/${rolePath}/dashboard`);
          }
        } catch {
          sessionStorage.removeItem("tvc_user");
          sessionStorage.removeItem("tvc_token");
        }
        setLoading(false);
      })();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    const rolePath = userData.role.toLowerCase();
    setActiveSection(`/${rolePath}/dashboard`);
    navigate(`/${rolePath}/dashboard`);
  };

  const handleLogout = async () => {
    const token = sessionStorage.getItem("tvc_token");
    if (token && user) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
      }
    }
    sessionStorage.removeItem("tvc_token");
    sessionStorage.removeItem("tvc_user");
    setUser(null);
    navigate("../login");
  };

  const handleNavigate = (path) => {
    setActiveSection(path);
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    navigate(`/${cleanPath}`);
  };

  if (loading) {
    return <div className="AdminLoginPage">Loading...</div>;
  }

  if (!user) {
    const isSignup = location.pathname.includes("signup");
    if (isSignup) {
      return <AdminSignUp />;
    }
    return <AdminLogin onLogin={handleLogin} />;
  }

  const rolePath = user.role.toLowerCase();

  const renderContent = () => {
    if (!activeSection) return <AdminDashboard user={user} onNavigate={handleNavigate} />;

    switch (activeSection) {
      case `/${rolePath}/dashboard`:
        return <AdminDashboard user={user} onNavigate={handleNavigate} />;

      case "/admin/user-approvals":
        return <AdminUserApproval />;
      case "/admin/refund-ratings":
        return <AdminReviews user={user} />;
      case "/admin/review-system":
        return (
          <div className="AdminCard">
            <div className="AdminCardHeader">
              <h3 className="AdminFooterH3">Review System</h3>
            </div>
            <p className="AdminText13 AdminTextMuted" style={{ padding: "12px 0" }}>
              Review system flow configuration coming soon.
            </p>
          </div>
        );
      case "/admin/staff-changes":
        return <AdminChangeRequests filterRole="Staff" />;
      case "/admin/manager-changes":
        return <AdminChangeRequests filterRole="Manager" />;
      case "/admin/my-changes":
        return <AdminMyChanges apiEndpoint="/admin/my-changes" />;
      case "/admin/change-requests":
        return <AdminChangeRequests />;
      case "/admin/login-history":
        return <AdminLoginHistory user={user} />;
      case "/admin/notifications":
        return <AdminNotificationPanel user={user} />;

      case "/manager/refund-ratings":
        return <AdminReviews user={user} />;
      case "/manager/review-system":
        return (
          <div className="AdminCard">
            <div className="AdminCardHeader">
              <h3 className="AdminFooterH3">Review System</h3>
            </div>
            <p className="AdminText13 AdminTextMuted" style={{ padding: "12px 0" }}>
              Review system flow configuration coming soon.
            </p>
          </div>
        );
      case "/manager/staff-changes":
        return <AdminStaffChanges />;
      case "/manager/my-changes":
        return <AdminMyChanges apiEndpoint="/manager/my-changes" />;
      case "/manager/login-history":
        return <AdminLoginHistory user={user} />;
      case "/manager/notifications":
        return <AdminNotificationPanel user={user} />;

      case "/staff/submit-change":
        return <AdminStaffSubmitChange />;
      case "/staff/my-changes":
        return <AdminMyChanges />;
      case "/staff/notifications":
        return <AdminNotificationPanel user={user} />;

      case `/${rolePath}/content-manager`:
        return <AdminContentManager user={user} onNavigate={handleNavigate} />;

      case `/${rolePath}/footer-editor`:
        return <AdminFooterEditor user={user} section={{ key: "footer", label: "Footer" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/navbar-editor`:
        return <AdminNavbarEditor user={user} section={{ key: "navbar", label: "Navbar" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/visa-detail-editor`:
        return <AdminVisaDetailEditor user={user} section={{ key: "visaDetail", label: "Visa Detail" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/refund-policy-editor`:
        return <AdminRefundPolicyEditor user={user} section={{ key: "refundPolicy", label: "Refund Policy" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/requirements-editor`:
        return <AdminRequirementsEditor user={user} section={{ key: "requirements", label: "Requirements" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/visa-career-editor`:
        return <AdminVisaCareerEditor user={user} section={{ key: "visaCareer", label: "Visa Career" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/schedule-meeting-editor`:
        return <AdminScheduleMeetingEditor user={user} section={{ key: "scheduleMeeting", label: "Schedule Meeting" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/government-fee-editor`:
        return <AdminGovernmentFeeEditor user={user} section={{ key: "governmentFee", label: "Government Fee" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/govt-fee-history-editor`:
        return <AdminGovtFeeHistoryEditor user={user} section={{ key: "governmentFee", label: "Government Fee History" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/job-applications`:
        return <AdminJobApplications user={user} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/meeting-bookings`:
        return <AdminMeetingBookings user={user} onBack={() => handleNavigate(`/${rolePath}/schedule-meeting-editor`)} />;

      case `/${rolePath}/email-settings`:
        return <AdminEmailSettingsEditor user={user} section={{ key: "emailSettings", label: "Email Settings" }} onBack={() => handleNavigate(`/${rolePath}/dashboard`)} />;

      case `/${rolePath}/hero-editor`:
        return <AdminHeroEditor user={user} section={{ key: "hero", label: "Hero" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/event-editor`:
        return <AdminEventEditor user={user} section={{ key: "event", label: "Event" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      case `/${rolePath}/passport-editor`:
        return <AdminPassportEditor user={user} section={{ key: "passport", label: "Passport Buddy" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      // Redirect old standalone editor paths to Visa Detail (now includes all tables as tabs)
      case `/${rolePath}/visa-offices-editor`:
      case `/${rolePath}/embassies-editor`:
      case `/${rolePath}/forms-editor`:
        return <AdminVisaDetailEditor user={user} section={{ key: "visaDetail", label: "Visa Detail" }} onBack={() => handleNavigate(`/${rolePath}/content-manager`)} />;

      default:
        return <AdminDashboard user={user} onNavigate={handleNavigate} />;
    }
  };

  return (
    <AdminLayout
      user={user}
      activeSection={activeSection}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
    >
      {renderContent()}
    </AdminLayout>
  );
}

export default AdminApp;
